import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaClock,
  FaFilePdf,
  FaUpload,
  FaCheckCircle,
  FaEye,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import AssignmentService from "../../../services/assignmentService";
import { API_BASE_URL } from "../../../services/api";

const StudentAssignmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionText, setSubmissionText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);

  useEffect(() => {
    loadAssignment();
  }, [id]);

  const loadAssignment = async () => {
    try {
      setLoading(true);
      const response = await AssignmentService.getStudentAssignment(id);
      console.log("Assignment data:", response.data);
      if (response.data) {
        setAssignment(response.data.assignment);
        setSubmission(response.data.submission);
        if (response.data.submission) {
          setSubmissionText(response.data.submission.submission_text || "");
          setExistingAttachment({
            path: response.data.submission.attachment_path,
            name: response.data.submission.attachment_name,
          });
        }
      }
    } catch (error) {
      console.error("Failed to load assignment:", error);
      toast.error("Failed to load assignment");
      navigate("/student/assignments");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleSubmit = async () => {
    if (!submissionText.trim() && !attachment) {
      toast.error("Please provide either text answer or upload a file");
      return;
    }

    const formData = new FormData();
    if (submissionText.trim()) {
      formData.append("submission_text", submissionText);
    }
    if (attachment) {
      formData.append("attachment", attachment);
    }

    try {
      setSubmitting(true);
      await AssignmentService.submitAssignment(id, formData);
      toast.success("Assignment submitted successfully");
      loadAssignment(); // Reload to get updated submission
      setAttachment(null);
    } catch (error) {
      console.error("Failed to submit assignment:", error);
      toast.error(
        error.response?.data?.message || "Failed to submit assignment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const viewFile = (filePath) => {
    if (!filePath) return;
    const url = `${API_BASE_URL}/${filePath}`;
    window.open(url, "_blank");
  };
  const downloadFile = (filePath, fileName) => {
    if (!filePath) return;
    const url = `${API_BASE_URL}/${filePath}`;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "assignment";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    try {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return date;
    }
  };

  const isOverdue = () => {
    if (!assignment || !assignment.due_date) return false;
    return new Date(assignment.due_date) < new Date();
  };

  const canSubmit = () => {
    if (!assignment) return false;
    if (assignment.status !== "published") return false;
    if (
      submission &&
      submission.obtained_marks !== null &&
      submission.obtained_marks !== undefined
    )
      return false;
    // Allow resubmission even if overdue, just mark as late
    return true;
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading assignment...</p>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div>
        <Header />
        <Container className="py-5 text-center">
          <Alert variant="danger">Assignment not found</Alert>
          <Button
            variant="primary"
            onClick={() => navigate("/student/assignments")}
          >
            Back to Assignments
          </Button>
        </Container>
      </div>
    );
  }

  const isGraded =
    submission &&
    submission.obtained_marks !== null &&
    submission.obtained_marks !== undefined;

  return (
    <div>
      <Header />
      <div className="student-assignment-detail">
        <Container className="py-4">
          {/* Back Button */}
          <Button
            variant="link"
            className="text-decoration-none mb-3"
            onClick={() => navigate("/student/assignments")}
          >
            <FaArrowLeft className="me-2" /> Back to Assignments
          </Button>

          <Row>
            <Col lg={8}>
              {/* Assignment Details */}
              <Card className="mb-4 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h2 className="card-title mb-0">{assignment.title}</h2>
                    <Badge
                      bg={
                        isGraded
                          ? "success"
                          : submission
                            ? "info"
                            : isOverdue()
                              ? "danger"
                              : "warning"
                      }
                      className="fs-6 px-3 py-2"
                    >
                      {isGraded
                        ? "✅ Graded"
                        : submission
                          ? "📤 Submitted"
                          : isOverdue()
                            ? "⚠️ Overdue"
                            : "⏳ Pending"}
                    </Badge>
                  </div>

                  <div className="assignment-meta-info mb-4">
                    <div className="meta-row">
                      <FaClock className="meta-icon" />
                      <span>
                        Due Date:{" "}
                        <strong>{formatDate(assignment.due_date)}</strong>
                        {isOverdue() && !submission && (
                          <Badge bg="danger" className="ms-2">
                            Past Due
                          </Badge>
                        )}
                      </span>
                    </div>
                    <div className="meta-row">
                      <strong>Total Marks:</strong> {assignment.total_marks}
                    </div>
                    {assignment.batch_name && (
                      <div className="meta-row">
                        <strong>Batch:</strong> {assignment.batch_name}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <h5 className="mt-4">📋 Description</h5>
                  <div className="assignment-description mb-4">
                    {assignment.description ? (
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {assignment.description}
                      </div>
                    ) : (
                      <p className="text-muted">No description provided.</p>
                    )}
                  </div>

                  {/* Assignment Attachment (from teacher) */}
                  {assignment.attachment_path && (
                    <div className="assignment-attachment mb-4">
                      <h5>📎 Reference Materials</h5>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => viewFile(assignment.attachment_path)}
                      >
                        <FaFilePdf className="me-2" />
                        {assignment.attachment_name || "Download Attachment"}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Submission Section */}
              <Card className="shadow-sm">
                <Card.Body>
                  <h4 className="mb-3">
                    {isGraded
                      ? "📊 Your Submission & Result"
                      : submission
                        ? "✏️ Update Submission"
                        : "📝 Submit Your Work"}
                  </h4>

                  {isGraded ? (
                    // Graded submission view
                    <div>
                      <Alert variant="success">
                        <FaCheckCircle className="me-2" />
                        <strong>Your assignment has been graded!</strong>
                      </Alert>
                      <div className="grade-result mb-3">
                        <h5>
                          Obtained Marks: {submission.obtained_marks}/
                          {assignment.total_marks}
                        </h5>
                        <div className="progress" style={{ height: "10px" }}>
                          <div
                            className="progress-bar bg-success"
                            style={{
                              width: `${(submission.obtained_marks / assignment.total_marks) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                      {submission.feedback && (
                        <div className="feedback-section mb-3">
                          <h6>💬 Feedback from Instructor:</h6>
                          <p className="bg-light p-3 rounded">
                            {submission.feedback}
                          </p>
                        </div>
                      )}
                      <div className="submission-details">
                        <h6>Your Submission:</h6>
                        {submission.submission_text && (
                          <p className="bg-light p-3 rounded">
                            {submission.submission_text}
                          </p>
                        )}
                        {submission.attachment_path && (
                          <div className="mt-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                viewFile(submission.attachment_path)
                              }
                            >
                              <FaEye className="me-2" /> View Your Submission
                              File
                            </Button>
                          </div>
                        )}
                        <div className="mt-3">
                          <small className="text-muted">
                            Submitted on: {formatDate(submission.submitted_at)}
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Submission form - ALWAYS SHOW THIS
                    <div>
                      <Alert variant="info">
                        <FaInfoCircle className="me-2" />
                        Submit your work before the due date. You can resubmit
                        multiple times.
                      </Alert>

                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            📝 Your Answer{" "}
                            <span className="text-muted">
                              (Optional if uploading file)
                            </span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={10}
                            value={submissionText}
                            onChange={(e) => setSubmissionText(e.target.value)}
                            placeholder="Write your answer here. You can use HTML, JavaScript, or any code..."
                            style={{
                              fontFamily: "monospace",
                              fontSize: "14px",
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>
                            📎 Upload File{" "}
                            <span className="text-muted">
                              (Optional if providing text)
                            </span>
                          </Form.Label>
                          <Form.Control
                            type="file"
                            onChange={handleFileChange}
                          />
                          {attachment && (
                            <div className="mt-2">
                              <Alert variant="success" className="py-1">
                                ✅ Selected: {attachment.name}
                              </Alert>
                            </div>
                          )}
                          {existingAttachment && !attachment && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Previously uploaded: {existingAttachment.name}
                              </small>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 ms-2"
                                onClick={() =>
                                  viewFile(existingAttachment.path)
                                }
                              >
                                View
                              </Button>
                            </div>
                          )}
                          <Form.Text className="text-muted">
                            Any file type is allowed (HTML, PDF, ZIP, DOC,
                            etc.). No size limit.
                          </Form.Text>
                        </Form.Group>

                        <Button
                          variant="primary"
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="w-100"
                          size="lg"
                        >
                          {submitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FaUpload className="me-2" />
                              {submission
                                ? "Resubmit Assignment"
                                : "Submit Assignment"}
                            </>
                          )}
                        </Button>
                      </Form>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Quick Info Card */}
              <Card className="shadow-sm mb-4">
                <Card.Body>
                  <h5 className="mb-3">ℹ️ Quick Information</h5>
                  <div className="quick-info-item">
                    <strong>Status:</strong>{" "}
                    <Badge
                      bg={
                        isGraded
                          ? "success"
                          : submission
                            ? "info"
                            : isOverdue()
                              ? "danger"
                              : "warning"
                      }
                    >
                      {isGraded
                        ? "Graded"
                        : submission
                          ? "Submitted"
                          : isOverdue()
                            ? "Overdue"
                            : "Pending"}
                    </Badge>
                  </div>
                  <div className="quick-info-item">
                    <strong>Due Date:</strong> {formatDate(assignment.due_date)}
                  </div>
                  <div className="quick-info-item">
                    <strong>Total Marks:</strong> {assignment.total_marks}
                  </div>
                  {isGraded && (
                    <div className="quick-info-item">
                      <strong>Your Score:</strong>{" "}
                      <span className="text-success fw-bold">
                        {submission.obtained_marks}/{assignment.total_marks}
                      </span>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Tips Card */}
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">💡 Tips for Success</h5>
                  <ul className="tips-list">
                    <li>Read the instructions carefully</li>
                    <li>Submit before the due date</li>
                    <li>You can resubmit multiple times</li>
                    <li>Check your feedback after grading</li>
                    <li>Contact your instructor if you have questions</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default StudentAssignmentDetail;
